import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

function fmtINR(n) {
  if (n === null || n === undefined || n === '') return '—';
  return '₹' + Math.round(Number(n)).toLocaleString('en-IN');
}

export default function LeadsDryRunPage() {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/leads/dry-run-client-link');
      setRows(data.candidates || []);
      setPicked({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleAll = (v) => {
    const m = {};
    if (v) rows.forEach((r) => { m[r.proposal_id] = true; });
    setPicked(m);
  };

  const anyPicked = Object.values(picked).some(Boolean);

  const apply = async () => {
    const ids = Object.entries(picked).filter(([_, v]) => v).map(([k]) => k);
    if (!ids.length) return;
    if (!window.confirm(`Apply client linking for ${ids.length} proposal(s)? Nothing is deleted.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post('/reports/leads/dry-run-client-link/apply', { proposal_ids: ids });
      toast.success(`Applied ${data.total} link(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="leads-dry-run-page">
      <PageHeader title="Leads → Clients dry-run" subtitle="Leads that have proposals but no linked client. Review before applying — nothing is changed until you press Apply." />

      <Card className="p-4 bg-white/80">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-ink-2">
            <b className="text-ink">{rows.length}</b> candidate{rows.length === 1 ? '' : 's'} found
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => toggleAll(true)} data-testid="ldr-select-all">Select all</Button>
            <Button variant="outline" onClick={() => toggleAll(false)} data-testid="ldr-select-none">Clear</Button>
            <Button disabled={!anyPicked || applying} onClick={apply} data-testid="ldr-apply">
              {applying ? 'Applying…' : 'Apply to selected'}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink/10 text-left text-ink-2">
                <th className="py-2 pr-2 w-10"></th>
                <th className="py-2 pr-2">Proposal</th>
                <th className="py-2 pr-2 text-right">Total</th>
                <th className="py-2 pr-2">Lead</th>
                <th className="py-2 pr-2">Email</th>
                <th className="py-2 pr-2">Phone</th>
                <th className="py-2 pr-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="py-6 text-center text-ink-2">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-ink-2">All leads with proposals are already linked to a client.</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-ink/5" data-testid={`ldr-row-${i}`}>
                  <td className="py-2 pr-2">
                    <Checkbox
                      checked={!!picked[r.proposal_id]}
                      onCheckedChange={(v) => setPicked({ ...picked, [r.proposal_id]: !!v })}
                      data-testid={`ldr-pick-${i}`}
                    />
                  </td>
                  <td className="py-2 pr-2 font-medium">
                    <Link to={`/proposals/${r.proposal_id}`} className="text-blue-700 hover:underline">{r.proposal_number}</Link>
                  </td>
                  <td className="py-2 pr-2 text-right">{fmtINR(r.proposal_total)}</td>
                  <td className="py-2 pr-2">
                    <div className="font-medium">{r.lead?.name || r.lead?.company || '—'}</div>
                    {r.lead?.company && r.lead?.name !== r.lead?.company && (
                      <div className="text-xs text-ink-2">{r.lead.company}</div>
                    )}
                  </td>
                  <td className="py-2 pr-2">{r.lead?.email || '—'}</td>
                  <td className="py-2 pr-2">{r.lead?.phone || '—'}</td>
                  <td className="py-2 pr-2">
                    {r.action === 'link'
                      ? <Badge className="bg-blue-100 text-blue-700 border-0">Link to “{r.existing_client?.name}”</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-700 border-0">Create new client</Badge>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
